<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-2xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <div v-if="fileInfo.company?.logoUrl" class="mb-4">
          <img :src="fileInfo.company.logoUrl" :alt="fileInfo.company.name" class="h-16 mx-auto" />
        </div>
        <h1 class="text-4xl font-bold text-gray-900 mb-2">
          {{ fileInfo.company?.name || 'FileDropLink' }}
        </h1>
        <p class="text-lg text-gray-600">
          Téléchargement de fichier sécurisé
        </p>
      </div>

      <!-- File Info Card -->
      <div v-if="fileInfo" class="card p-8 mb-6">
        <div class="text-center mb-6">
          <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">
            {{ fileInfo.fileName }}
          </h2>
          <p class="text-gray-600">
            {{ formatFileSize(fileInfo.fileSize) }}
          </p>
        </div>

        <!-- File Details -->
        <div class="space-y-4 border-t pt-6">
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Envoyé par:</span>
              <p class="font-medium">{{ fileInfo.uploadedBy || 'Anonyme' }}</p>
            </div>
            <div>
              <span class="text-gray-500">Expire le:</span>
              <p class="font-medium">{{ formatDate(fileInfo.expirationDate) }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Téléchargements:</span>
              <p class="font-medium">{{ fileInfo.downloadCount }} / {{ fileInfo.maxDownloads }}</p>
            </div>
            <div>
              <span class="text-gray-500">Envoyé le:</span>
              <p class="font-medium">{{ formatDate(fileInfo.createdAt) }}</p>
            </div>
          </div>
        </div>

        <!-- Download Button -->
        <div class="mt-8 text-center">
          <button
            @click="downloadFile"
            :disabled="downloading"
            class="btn-primary text-lg px-8 py-3 disabled:opacity-50"
          >
            <span v-if="downloading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Téléchargement...
            </span>
            <span v-else class="flex items-center">
              <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Télécharger le fichier
            </span>
          </button>
        </div>
      </div>

      <!-- Error Card -->
      <div v-if="error" class="card p-8 text-center">
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">
          {{ error.title || 'Erreur' }}
        </h2>
        <p class="text-gray-600 mb-6">
          {{ error.message }}
        </p>
        <button
          @click="$router.push('/')"
          class="btn-primary"
        >
          Retour à l'accueil
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="card p-8 text-center">
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
          <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">
          Chargement...
        </h2>
        <p class="text-gray-600">
          Récupération des informations du fichier
        </p>
      </div>

      <!-- Footer -->
      <div class="text-center mt-8 text-sm text-gray-500">
        <p>Fichier sécurisé par {{ fileInfo.company?.name || 'FileDropLink' }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DownloadPage',
  data() {
    return {
      fileInfo: null,
      loading: true,
      downloading: false,
      error: null
    }
  },
  async mounted() {
    await this.loadFileInfo()
  },
  methods: {
    async loadFileInfo() {
      try {
        const fileId = this.$route.params.id
        const response = await axios.get(`/api/files/${fileId}`)
        this.fileInfo = response.data
      } catch (error) {
        console.error('Error loading file info:', error)
        
        if (error.response?.status === 404) {
          this.error = {
            title: 'Fichier introuvable',
            message: 'Le fichier que vous recherchez n\'existe pas ou a été supprimé.'
          }
        } else if (error.response?.status === 410) {
          this.error = {
            title: 'Fichier expiré',
            message: 'Ce fichier a expiré et n\'est plus disponible pour le téléchargement.'
          }
        } else {
          this.error = {
            title: 'Erreur',
            message: 'Une erreur est survenue lors du chargement du fichier.'
          }
        }
      } finally {
        this.loading = false
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async downloadFile() {
      this.downloading = true
      
      try {
        const fileId = this.$route.params.id
        const response = await axios.get(`/api/files/${fileId}/download`)
        
        if (response.data.downloadUrl) {
          // For S3 files, redirect to signed URL
          window.location.href = response.data.downloadUrl
        } else {
          // For local files, the response should be the file stream
          // This is handled by the backend
          window.location.href = `/api/files/${fileId}/download`
        }
      } catch (error) {
        console.error('Download error:', error)
        alert('Erreur lors du téléchargement du fichier')
      } finally {
        this.downloading = false
      }
    }
  }
}
</script> 